/**
 * DropsRow
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import type { SmswiserDrop } from '@/types/smswiser-drops';
import { useToast } from '@/providers/ToastProvider';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';
import { deriveDropStatusLabel, getStatusBadgeClasses, getStatusIcon } from '@/features/drops/utils/dropStatus';
import { formatSmswiserDate } from '@/features/drops/utils/smswiserDate';
import { cn } from '@/lib/utils';

interface DropsRowProps {
  drop: SmswiserDrop;
  /** When set, Back on drop page returns here (e.g. /smswiser/australia?tab=deployed) */
  returnTo?: string;
}

const copyableButton =
  'inline-flex items-center gap-1 rounded px-1 -mx-1 py-0.5 hover:bg-muted/60 transition-colors cursor-pointer';

export const DropsRow: FC<DropsRowProps> = ({ drop, returnTo }) => {
  const { success: toastSuccess, error: toastError } = useToast();

  const handleCopy = useCallback(
    async (value: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(value);
        toastSuccess('Copied', 'Copied to clipboard');
      } catch {
        toastError('Copy failed', 'Could not copy to clipboard');
      }
    },
    [toastSuccess, toastError],
  );

  const listName = drop.leadsList || drop.campaign || '—';
  const secondaryText = drop.campaign || '—';
  const prefersReducedMotion = useReducedMotion();
  const statusLabel = deriveDropStatusLabel(drop);
  const sender = (drop.senderName || []).join(', ');
  const flagEmoji = countryToFlagEmoji(drop.country);
  const statusIcon = getStatusIcon(statusLabel);

  const timing = drop.status?.startedAt?.trim()
    ? formatSmswiserDate(drop.status.startedAt)
    : drop.executeAfter?.trim()
      ? formatSmswiserDate(drop.executeAfter)
      : drop.finishedAt?.trim()
        ? formatSmswiserDate(drop.finishedAt)
        : '—';

  return (
    <Link
      to={`/drops/${encodeURIComponent(drop.uniqueId)}`}
      state={{ drop, ...(returnTo ? { returnTo } : {}) }}
      className="block"
    >
      <motion.div
        initial={prefersReducedMotion ? false : { opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={prefersReducedMotion ? undefined : { opacity: 0, y: -6 }}
        className="px-4 py-2 hover:bg-muted/40 transition-colors"
      >
        <div className="grid grid-cols-12 gap-3 items-center">
          <div className="col-span-12 lg:col-span-7 min-w-0">
            <div className="flex items-start gap-2.5 min-w-0">
              <div
                className="h-8 w-8 rounded-lg border border-border bg-muted/30 flex items-center justify-center text-base"
                aria-label={`${drop.country} flag`}
                title={drop.country}
              >
                {drop.countryFlag ? (
                  <img
                    src={drop.countryFlag}
                    alt={`${drop.country} flag`}
                    className="h-5 w-5 object-contain"
                    loading="lazy"
                  />
                ) : (
                  flagEmoji
                )}
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-2 shrink-0">
                    {statusIcon}
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${getStatusBadgeClasses(
                        statusLabel,
                      )}`}
                    >
                      {statusLabel}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => handleCopy(drop.uniqueId, e)}
                    className={cn(
                      copyableButton,
                      'font-semibold font-mono text-sm whitespace-normal break-words leading-snug',
                    )}
                    title="Click to copy drop ID"
                  >
                    {drop.uniqueId}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleCopy(listName, e)}
                    className={cn(copyableButton, 'text-xs text-muted-foreground whitespace-normal break-words')}
                    title="Click to copy list name"
                  >
                    {listName}
                  </button>
                </div>

                <div className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                  <div className="whitespace-normal break-words">{secondaryText}</div>
                  <div className="truncate">
                    Sender: <span className="text-foreground/80">{sender || '—'}</span>
                  </div>
                </div>

                {/* Mobile meta row */}
                <div className="mt-1.5 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 lg:hidden">
                  <span>
                    Country: <span className="text-foreground/80">{drop.country}</span>
                  </span>
                  <span>
                    Amount: <span className="text-foreground/80">{drop.cap.toLocaleString()}</span>
                  </span>
                  <span>
                    Timing: <span className="text-foreground/80">{timing}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-2">
            <p className="text-xs text-muted-foreground">Country</p>
            <p className="text-sm whitespace-normal break-words">{drop.country}</p>
          </div>

          <div className="hidden lg:block lg:col-span-1 lg:text-right">
            <p className="text-xs text-muted-foreground">Amount</p>
            <p className="text-sm font-medium">{drop.cap.toLocaleString()}</p>
          </div>

          <div className="hidden lg:block lg:col-span-2 lg:text-right">
            <p className="text-xs text-muted-foreground">Timing</p>
            <p className="text-sm text-muted-foreground whitespace-normal break-words leading-snug">{timing}</p>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

export default DropsRow;

